import { Injectable } from '@angular/core';

/**
 * A key chord: `key` as in `KeyboardEvent.key` (case-insensitive) plus the
 * modifiers that must be held. `ctrl` means Ctrl on Windows / Linux and Cmd on
 * macOS. `shift` is only checked when given - a `/` is Shift+7 on some layouts.
 */
export interface KeyChord {
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
}

/**
 * Global keyboard shortcuts (WAP-1692). One capture-phase `keydown` listener on
 * `window`, like EscapeManager, and a registry of chords. A handler returns
 * true when it consumed the event; the event is then stopped so neither the
 * browser nor the focused control sees it. Register from a component that
 * lives as long as the shortcut should (the header for app-wide chords) and
 * unregister on destroy. Chords with a modifier fire inside inputs too - that
 * is the point of a global command key.
 */
@Injectable({ providedIn: 'root' })
export class KeyboardShortcutService {
  private handlers: Array<{
    id: symbol;
    chord: KeyChord;
    handler: () => boolean;
  }> = [];
  private listening = false;

  register(chord: KeyChord, handler: () => boolean): symbol {
    const id = Symbol();
    this.handlers.push({ id, chord, handler });
    this.startListening();
    return id;
  }

  unregister(id: symbol): void {
    this.handlers = this.handlers.filter((h) => h.id !== id);
  }

  private matches(chord: KeyChord, event: KeyboardEvent): boolean {
    const primary = event.ctrlKey || event.metaKey;
    return (
      event.key.toLowerCase() === chord.key.toLowerCase() &&
      primary === !!chord.ctrl &&
      event.altKey === !!chord.alt &&
      (chord.shift === undefined || event.shiftKey === chord.shift)
    );
  }

  private handleKeydown = (event: KeyboardEvent): void => {
    if (event.repeat || event.isComposing) return;
    for (const { chord, handler } of this.handlers) {
      if (this.matches(chord, event) && handler()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        break;
      }
    }
  };

  private startListening(): void {
    if (this.listening) return;
    window.addEventListener('keydown', this.handleKeydown, {
      capture: true,
      passive: false,
    });
    this.listening = true;
  }
}
